import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// How long a user can be inactive before being auto logged out.
const IDLE_LIMIT_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Signs the user out and redirects them after IDLE_LIMIT_MS of no
 * mouse/keyboard/touch/scroll activity. Only runs while `active` is true,
 * so call this with `active = isLoggedIn` (no point tracking idle time on
 * a login screen nobody is "logged into" yet).
 */
export function useIdleLogout(active: boolean, redirectTo: string) {
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!active) return;

    const logoutForInactivity = async () => {
      await supabase.auth.signOut();
      navigate(redirectTo);
    };

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logoutForInactivity, IDLE_LIMIT_MS);
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(evt => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [active, redirectTo, navigate]);
}
